import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const ordersApi = createApi({
  reducerPath: "orders",
  tagTypes: ["orders", "newOrders", "newFulfilledOrders"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    createOrder: builder.query<ApiTypes.Model.Order, ApiTypes.Req.CreateOrder>({
      query: body => ({
        url: `/orders`,
        method: "POST",
        body,
      }),
    }),
    editNewOrder: builder.query<ApiTypes.Model.Order, { data: ApiTypes.Req.CreateOrder; id: number }>({
      query: body => ({
        url: `/orders/${body.id}/new`,
        method: "POST",
        body: body.data,
      }),
    }),
    fulfillOrder: builder.query<ApiTypes.Model.Order, number>({
      query: id => ({
        url: `/orders/${id}/fulfill`,
        method: "POST",
        headers: { accept: "application/json" },
      }),
    }),
    deleteNewOrder: builder.query<ApiTypes.Model.Order, number>({
      query: id => ({
        url: `/orders/${id}/new`,
        method: "DELETE",
      }),
    }),
    getOrders: builder.query<ApiTypes.Res.Orders, UITypes.Params>({
      query: arg => {
        return {
          url: `/orders`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getFulfilledOrders: builder.query<ApiTypes.Res.Orders, UITypes.Params>({
      query: arg => {
        return {
          url: `/orders/fulfilled`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getNewOrdersQuantity: builder.query<{ data: { quantity: number } }, void>({
      query: () => {
        return {
          url: `/orders/new-quantity`,
          method: "GET",
          headers: { Accept: "application/json" },
        }
      },
    }),
    getNewOrders2Quantity: builder.query<{ data: { quantity: number } }, void>({
      query: () => {
        return {
          url: `/orders2/new-quantity`,
          method: "GET",
          headers: { Accept: "application/json" },
        }
      },
    }),
    // New Orders API section
    getOrdersNew: builder.query<ApiTypes.Res.OrdersNew, UITypes.Params>({
      query: arg => {
        return {
          url: `/orders2`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["newOrders"],
    }),
    getFulfilledOrdersNew: builder.query<ApiTypes.Res.OrdersNew, UITypes.Params>({
      query: arg => {
        return {
          url: `/orders2/fulfilled`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    deleteNewOrderV2: builder.mutation<ApiTypes.Model.Order, number>({
      query: id => ({
        url: `/orders2/${id}/new`,
        method: "DELETE",
      }),
      invalidatesTags: ["newOrders"],
    }),
    fulfillOrderNew: builder.mutation<ApiTypes.Model.Order, number>({
      query: id => ({
        url: `/orders2/item/${id}/fulfill`,
        method: "POST",
      }),
      invalidatesTags: ["newOrders"],
    }),
    getOrderDevices: builder.query<{ data: string[] }, number>({
      query: id => ({
        url: `/orders2/item/${id}/devices`,
        method: "GET",
      }),
    }),
    createOrderNew: builder.mutation<ApiTypes.Model.OrderNew, ApiTypes.Req.CreateOrderNew>({
      query: body => ({
        url: `/orders2`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["newOrders"],
    }),
    editOrderNew: builder.mutation<ApiTypes.Model.OrderNew, { data: ApiTypes.Req.CreateOrderNew; id: number }>({
      query: body => ({
        url: `/orders2/${body.id}/new`,
        method: "POST",
        body: body.data,
      }),
      invalidatesTags: ["newOrders"],
    }),
  }),
})

export const {
  useLazyCreateOrderQuery,
  useCreateOrderNewMutation,
  useLazyGetOrdersQuery,
  useLazyGetOrdersNewQuery,
  useLazyGetFulfilledOrdersNewQuery,
  useLazyEditNewOrderQuery,
  useEditOrderNewMutation,
  useLazyDeleteNewOrderQuery,
  useDeleteNewOrderV2Mutation,
  useLazyFulfillOrderQuery,
  useFulfillOrderNewMutation,
  useLazyGetOrderDevicesQuery,
  useLazyGetFulfilledOrdersQuery,
  useLazyGetNewOrdersQuantityQuery,
  useLazyGetNewOrders2QuantityQuery,
} = ordersApi

export const getOrders = ordersApi.endpoints.getOrders
